import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import { NotificationManager } from 'react-notifications'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import plusIcon from '../../../assets/svgs/plus.svg'
import AreYouSureModal from '../../../components/common/areYouSureModal'
import LoadingScreen from '../../../components/custom/LoadingScreen'
import GenericPagination from '../../../components/generic-table/generic-pagination'
import GenericTable from '../../../components/generic-table/generic-table'
import TableEmptyData from '../../../components/table/TableEmptyData'
import postalPricingService from '../../../services/postalPricingService'
import { identifyVenue, trackEvent } from '../../../utils/mixpanelUtil'
import PostalPricingModal from './postal-pricing-modal'
import PostalTypesSection from './postal-types-section'

export const postalText = (text) => t(`postal.${text}`)

const PostalPricing = ({ }) => {
   const [showAddPostal, setShowAddPostal] = useState(false)
   const [isAreYouSureDeleteModalOpen, setAreYouSureDeleteModalOpen] = useState(false)
   const [page, setPage] = useState(0)
   const [rowsPerPage, setRowsPerPage] = useState(10)
   const [loaded, setLoaded] = useState(true)
   const { short_code } = useSelector((state) => state.app)
   const [selectedPostal, setSelectedPostal] = useState()
   const [recordDetails, setRecordDetails] = useState()
   const [postalPricing, setPostalPricing] = useState([])
   const [postalTypes, setPostalTypes] = useState([])
   const [cities, setCities] = useState([])
   const [pagination, setPagination] = useState({
      currentPage: 0,
      totalPages: 0,
      totalItems: 0,
      perPage: 10
   })

   const navigate = useNavigate()

   const columns = [
      { label: 'City', field: 'city.name', align: 'left' },
      { label: 'Postal Type', field: 'postal.name', align: 'left' },
      { label: 'Alpha ID', field: 'alpha_id', align: 'left' },
      { label: 'Description', field: 'alpha_description', align: 'left' },
      { label: 'Price', field: 'price', align: 'left' },
      { label: 'Price Without Tax', field: 'price_without_tax', align: 'left' },
      {
         label: 'Status',
         field: 'status',
         align: 'left',
         cellComponent: (row) => (
            <span className={`px-2 py-1 rounded-full text-xs ${row.data ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
               {row.data ? 'Active' : 'Inactive'}
            </span>
         ),
      },
      { label: 'Actions', field: 'actions', align: 'right' },
   ]

   const goToPostal = () => navigate(`/${short_code}/admin/store-settings/postal-pricing`)

   const getAllPostalPricing = (currentPage = 1) => {
      setLoaded(false)
      postalPricingService
         .getPostalPricing({
            venue_short_code: short_code,
            page: currentPage,
            per_page: rowsPerPage
         })
         .then((res) => {
            identifyVenue(short_code)
            trackEvent('PostalPricing', {
               action_category: 'get',
               action_outcome: 'success',
               interaction_element: 'postal-pricing',
            })
            setLoaded(true)
            setPostalPricing(res?.data ?? [])

            // Set pagination data from meta
            if (res?.meta) {
               setPagination({
                  currentPage: res.meta.current_page - 1, // Adjust for zero-based index
                  totalPages: res.meta.last_page,
                  totalItems: res.meta.total,
                  perPage: res.meta.per_page
               })
            }
         })
         .catch(() => {
            identifyVenue(short_code)
            trackEvent('PostalPricing', {
               action_category: 'get',
               action_outcome: 'fail',
               interaction_element: 'postal-pricing',
            })
            setLoaded(true)
            NotificationManager.error(
               'Failed to get postal pricing list',
               'Error',
               3000
            )
         })
   }

   const getPostalTypes = () => {
      postalPricingService
         .getPostalTypes()
         .then((res) => {
            setPostalTypes(res?.data ?? [])
         })
         .catch(() => {
            NotificationManager.error(
               'Failed to get postal types',
               'Error',
               3000
            )
         })
   }

   const getCities = () => {
      postalPricingService
         .getCities()
         .then((res) => {
            setCities(res?.data ?? [])
         })
         .catch(() => {
            NotificationManager.error(
               'Failed to get cities',
               'Error',
               3000
            )
         })
   }

   const handleChangePage = (event, newPage) => {
      setPage(newPage)
      getAllPostalPricing(newPage + 1) // API expects 1-based page index
   }

   const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(event)
      setPage(0)
      getAllPostalPricing(1) // Reset to first page with new page size
   }

   const onEdit = (row) => {
      setShowAddPostal(true)
      setRecordDetails(row)
   }

   const onDelete = (id) => {
      setSelectedPostal(id)
      setAreYouSureDeleteModalOpen(true)
   }

   const onStatusUpdate = (row) => {
      postalPricingService
         .updatePostalPricingStatus(
            row.id,
            { status: !row.status },
            { venue_short_code: short_code }
         )
         .then(() => {
            NotificationManager.success(
               `Postal pricing status ${!row.status ? 'activated' : 'deactivated'} successfully`,
               'Success',
               3000
            )
            getAllPostalPricing(pagination.currentPage + 1) // Refresh current page
         })
         .catch((error) => {
            NotificationManager.error(
               error.message || 'Failed to update status',
               'Error',
               3000
            )
         })
   }

   useEffect(() => {
      getAllPostalPricing()
      getPostalTypes()
      getCities()
   }, [])

   const handleDeletePostal = () => {
      postalPricingService
         .deletePostalPricing(selectedPostal, { venue_short_code: short_code })
         .then(() => {
            identifyVenue(short_code)
            trackEvent('PostalPricing', {
               action_category: 'delete',
               action_outcome: 'success',
               interaction_element: 'postal-pricing',
            })
            NotificationManager.success(
               'Postal pricing has been deleted successfully',
               'Success',
               3000
            )
            setAreYouSureDeleteModalOpen(false)
            setRecordDetails(undefined)

            // Check if current page has only one item left and we're not on the first page
            if (postalPricing.length === 1 && pagination.currentPage > 0) {
               // Go to previous page after deletion if this was the last item on the page
               setPage(pagination.currentPage - 1);
               getAllPostalPricing(pagination.currentPage); // Backend page is 1-based
            } else {
               // Refresh current page
               getAllPostalPricing(pagination.currentPage + 1);
            }
         })
         .catch((error) => {
            identifyVenue(short_code)
            trackEvent('PostalPricing', {
               action_category: 'delete',
               action_outcome: 'fail',
               interaction_element: 'postal-pricing',
            })
            NotificationManager.error(
               error.message || t('common.error_generic'),
               'Error',
               3000
            )
         })
   }

   const getActionButtons = (row) => {
      return [
         {
            label: 'Edit',
            onClick: () => onEdit(row),
            className: 'bg-blue-500 text-white',
         },
         {
            label: row.status ? 'Deactivate' : 'Activate',
            onClick: () => onStatusUpdate(row),
            className: row.status ? 'bg-orange-500 text-white' : 'bg-green-500 text-white',
         },
         {
            label: 'Delete',
            onClick: () => onDelete(row.id),
            className: 'bg-red-500 text-white',
         },
      ]
   }

   return (
      <div>
         {showAddPostal && (
            <PostalPricingModal
               open={showAddPostal}
               onClose={() => {
                  setShowAddPostal(false)
                  setRecordDetails(null)
               }}
               postalDetails={recordDetails}
               fetchPostalPricing={() => getAllPostalPricing(pagination.currentPage + 1)}
               setPostalDetails={setRecordDetails}
               postalTypes={postalTypes}
               cities={cities}
               short_code={short_code}
            />
         )}

         {isAreYouSureDeleteModalOpen && (
            <AreYouSureModal
               title={postalText('delete_postal')}
               content={postalText('delete_postal_confirmation')}
               firstButton={postalText('no_go_back')}
               secondButton={postalText('yes_i_am')}
               showModal={isAreYouSureDeleteModalOpen}
               onClose={() => setAreYouSureDeleteModalOpen(false)}
               onSubmit={handleDeletePostal}
            />
         )}

         {loaded ? (
            <div className="flex flex-col h-full p-4">
               <div className="mb-8">
                  <PostalTypesSection
                     postalTypes={postalTypes}
                  />
               </div>

               <div className="flex flex-row justify-between items-center pl-4 pr-3 pt-5">
                  <div className="flex flex-row items-center">
                     <span className="text-black1 text-xl font-medium">
                        {postalText('Postal_Pricing')}
                     </span>
                     <span className="bg-blue2 text-black1 px-2 rounded-xl ml-2 font-medium">
                        {pagination.totalItems} {postalText('pricing_configs')}
                     </span>
                  </div>
                  <div className="flex items-center gap-1">
                     <div
                        className="bg-black1 flex flex-row px-6 py-2 text-white rounded-lg cursor-pointer"
                        onClick={() => setShowAddPostal(true)}
                     >
                        <img src={plusIcon} alt="add" className="mr-2" />
                        <span>{postalText('add_postal_pricing')}</span>
                     </div>
                  </div>
               </div>
               {postalPricing.length > 0 ? (
                  <div className="flex flex-col overflow-auto max-h-[70vh]">
                     <GenericTable
                        columns={columns}
                        data={postalPricing}
                        onDelete={onDelete}
                        onEdit={onEdit}
                        getActionButtons={getActionButtons}
                     />
                     <GenericPagination
                        count={pagination.totalItems}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        setPage={setPage}
                     />
                  </div>
               ) : (
                  <div className="border border-x-0 border-b-0 mt-5 mb-10">
                     <TableEmptyData
                        content={'postal pricing'}
                        btnLabel={'Add postal pricing'}
                        onAction={() => setShowAddPostal(true)}
                     />
                  </div>
               )}
            </div>
         ) : (
            <LoadingScreen />
         )}
      </div>
   )
}

export default PostalPricing